import { useState, useCallback, memo } from 'react';
import { Button, Menu, MenuItem, ListItemText } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useTranslation } from 'react-i18next';
import { useSearchItems } from 'components/Search/components/SearchItemsProvider';
import { EditorialAssetSearchResultItem } from 'utility/types/dataProvider';
import { useAddToRelationship } from '../hooks/useAddToRelationship';
import type { RelationshipType } from '../utils/relationshipUtils';

type AddToRelationshipButtonProps = {
  onClose?: () => void;
};

/**
 * Dropdown button that allows adding selected search items to a specific relationship type.
 * Automatically handles relationship cleanup (removes from other types when adding).
 * Used in the relationships search modal footer.
 */
export const AddToRelationshipButton = memo(
  ({ onClose }: AddToRelationshipButtonProps) => {
    const { t } = useTranslation();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const { selectedItems, clearSelectedItems } =
      useSearchItems<EditorialAssetSearchResultItem>();
    const addToRelationship = useAddToRelationship({ onClose: onClose });

    const open = Boolean(anchorEl);
    const hasSelectedItems = selectedItems && selectedItems.length > 0;

    const handleClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    }, []);

    const handleClose = useCallback(() => {
      setAnchorEl(null);
    }, []);

    const handleAddTo = useCallback(
      (relationship: RelationshipType) => {
        if (!selectedItems || selectedItems.length === 0) {
          return;
        }

        addToRelationship(selectedItems, relationship);
        clearSelectedItems();
        handleClose();
      },
      [selectedItems, addToRelationship, clearSelectedItems, handleClose],
    );

    const relationshipOptions = [
      {
        value: 'children' as RelationshipType,
        label: t('blox.editorial.asset.children.label'),
      },
      {
        value: 'parents' as RelationshipType,
        label: t('blox.editorial.asset.parents.label'),
      },
      {
        value: 'siblings' as RelationshipType,
        label: t('blox.editorial.asset.siblings.label'),
      },
    ];

    return (
      <>
        <Button
          data-testid="editorial-search-add-to-relationship"
          aria-label="add to relationship"
          variant="contained"
          onClick={handleClick}
          disabled={!hasSelectedItems}
          endIcon={<ArrowDropDownIcon />}
          aria-controls={open ? 'add-to-relationship-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          disableElevation
        >
          {t('blox.actions.add_to')}
        </Button>
        <Menu
          id="add-to-relationship-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          slotProps={{
            list: {
              'aria-labelledby': 'add-to-relationship-button',
            },
          }}
        >
          {relationshipOptions.map((option) => (
            <MenuItem
              key={option.value}
              onClick={() => handleAddTo(option.value)}
              data-testid={`add-to-${option.value}`}
            >
              <ListItemText>{option.label}</ListItemText>
            </MenuItem>
          ))}
        </Menu>
      </>
    );
  },
);

AddToRelationshipButton.displayName = 'AddToRelationshipButton';
